import React, { useMemo } from "react";
import { getQueueValuesAtTime } from "./timelineUtils";
import { isLambdaStage } from "./layout";

function ResourceBlock({ label, name, children }) {
  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-2 mb-1.5">
        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
          {label}
        </span>
      </div>
      <p className="text-xs text-gray-700 break-all font-mono leading-snug">{name}</p>
      {children && <div className="mt-2 pt-2 border-t border-gray-100">{children}</div>}
    </div>
  );
}

function MetricRow({ label, value }) {
  return (
    <div className="flex justify-between gap-4 text-xs">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-800">{value}</span>
    </div>
  );
}

const StageDetailPanel = ({
  pipeline,
  selectedStageKey,
  metrics,
  selectedTimestamp,
}) => {
  const stage = pipeline?.stages?.find((s) => s.key === selectedStageKey);

  const valuesAtTime = useMemo(
    () => getQueueValuesAtTime(metrics, pipeline, selectedTimestamp),
    [metrics, pipeline, selectedTimestamp]
  );

  if (!stage) return null;

  const atTime = valuesAtTime[stage.key] ?? {};

  if (isLambdaStage(stage)) {
    return (
      <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
        <h4 className="text-sm font-semibold text-gray-800 mb-2">{stage.shortName}</h4>
        <ResourceBlock label="Lambda" name={stage.functionName}>
          <MetricRow label="Invocations" value={atTime.invocations ?? 0} />
        </ResourceBlock>
      </div>
    );
  }

  return (
    <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
      <h4 className="text-sm font-semibold text-gray-800 mb-2">{stage.shortName}</h4>
      <div className="grid gap-2 sm:grid-cols-2">
        <ResourceBlock label="SQS" name={stage.queue}>
          <div className="space-y-1">
            <MetricRow label="Sent" value={atTime.sent ?? 0} />
            <MetricRow label="Deleted" value={atTime.deleted ?? 0} />
          </div>
        </ResourceBlock>
        {stage.dlq && (
          <ResourceBlock label="DLQ" name={stage.dlq}>
            <MetricRow label="In queue" value={atTime.dlqVisible ?? 0} />
          </ResourceBlock>
        )}
      </div>
    </div>
  );
};

export default StageDetailPanel;
